import {
  getCurrencySymbol,
  isCryptoCurrencyCode,
  isPrefixCurrencySymbol,
} from './currencySymbols.js';

function formatAmountDigits(n, precision) {
  const p = Math.max(0, Math.min(20, Math.floor(precision)));
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: p,
    maximumFractionDigits: p,
  }).format(n);
}

function formatNumberCore(amount, code, precision) {
  const n = Number(amount) || 0;
  if (precision != null && Number.isFinite(Number(precision))) {
    return formatAmountDigits(n, precision);
  }
  if (isCryptoCurrencyCode(code)) {
    if (n === 0) return '0';
    if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (n >= 1) return parseFloat(n.toFixed(4)).toString();
    return parseFloat(n.toFixed(8)).toString();
  }
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatWithSymbol(amount, currencyCode, precision, currencySymbol) {
  const sym =
    currencySymbol != null && String(currencySymbol).trim() !== ''
      ? String(currencySymbol).trim()
      : getCurrencySymbol(currencyCode);
  const formatted = formatNumberCore(amount, currencyCode, precision);
  if (isPrefixCurrencySymbol(sym)) return `${sym}${formatted}`;
  return `${sym} ${formatted}`;
}

/** Move a trailing currency token before the numeric amount: "100.00 FUN" → "FUN 100.00". */
export function moveCurrencyBeforeAmount(text) {
  const s = String(text ?? '').trim();
  const m = s.match(/^([\d,]+(?:\.\d+)?)\s+(\S+)$/);
  if (m) return `${m[2]} ${m[1]}`;
  return s;
}

const CURRENCY_CODE_ALIASES = {
  WON: 'KRW',
  CFA: 'XAF',
  MBTC: 'BTC',
  ZMK: 'ZMW',
};

export function normalizeCurrencyCode(rawCode) {
  const code = String(rawCode ?? '').trim().toUpperCase();
  return CURRENCY_CODE_ALIASES[code] ?? code;
}

export function formatCurrencyAmount(amount, currencyCode, precision, currencySymbol) {
  const code = normalizeCurrencyCode(currencyCode);
  return formatWithSymbol(amount, code, precision, currencySymbol);
}

export function formatCurrencyRulesLabel(currencyCode, currencySymbol) {
  if (currencySymbol != null && String(currencySymbol).trim() !== '') {
    return String(currencySymbol).trim();
  }
  const code = normalizeCurrencyCode(currencyCode);
  return code ? getCurrencySymbol(code) : '';
}

export function formatCurrencyDigits(amount, precision) {
  return formatAmountDigits(Number(amount) || 0, precision);
}

/**
 * Big-win overlay / compact win pill: en-US digit grouping; show currency symbol when possible.
 */
export function formatCurrencyCelebrationAmount(amount, currencyCode, precision, currencySymbol) {
  const n = Math.max(0, Number(amount) || 0);
  const pRaw = Math.floor(Number(precision));
  const p = Number.isFinite(pRaw) ? Math.max(0, Math.min(20, pRaw)) : 2;
  const code = normalizeCurrencyCode(currencyCode);
  return formatWithSymbol(n, code, p, currencySymbol);
}
